import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./FactoryOrder.scss";
import FetchData, { CreateEntry, GetListAsync, GetOne, UpdateEntry } from "../../providers/dataProvider";
import { DropDownComponent } from "../../dropdown/DropDownComponent";
import { PlaceHolder } from "../../constants/PlaceHolderConstants";
import { CheckNull } from "../../utils/Validate";
import { useRolePermission } from "../../hooks/useRolePermission";
import SaveSuccessModel from "../../components/common/SaveSuccessModel";
import ErrorMessage from "../../components/common/ErrorMessage";
import FactoryOrderProduct from "./FactoryOrderProduct";
import { deliveryLeadTimeValues } from "../../components/common/DeliveryLeadTime";
import AmountModeField from "../../components/common/AmountModeField";

const ExampleCustomInput = forwardRef(({ value, onClick }, ref) => (
  <input
    value={value}
    className="example-custom-input form-control"
    onClick={onClick}
    ref={ref}
    placeholder="DD/MM/YYYY"
    readOnly
  />
));

const createEmptyReleaseForm = () => ({
  scheduledDeliveryDate: new Date(),
  remarks: "",
  items: [],
   deliveryFromDate: null,
  deliveryToDate: null,
});

const EMPTY_FACTORY_VALUES = {
  products: [],
};

const EMPTY_QUOT_USER_ENTRY = {
  specialCharges: 0,
  freightCharges: 0,
  customDuty: 0,
  customDutyPercentage: 0,
  clearanceCharges: 0,
  clearanceChargesPercentage: 0,
  salesMarginPercentage: 0,
  tax: 0,
  taxPercentage: 0,
};

const flattenCustomerOrderItems = (products = []) =>
  (products || []).flatMap((product) => {
    if (Array.isArray(product?.productItems) && product.productItems.length > 0) {
      return product.productItems.map((item) => ({
        ...item,
        markName: product?.markName || item?.markName || "-",
        productName: product?.productName || item?.productName || "-",
        groupName: product?.groupName || item?.groupName || "-",
      }));
    }

    return [{
      ...product,
      markName: product?.markName || "-",
      productName: product?.productName || "-",
      groupName: product?.groupName || "-",
    }];
  });

const formatDateValue = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-GB");
};

const toLocalDateIso = (value, fallback = null) => {
  const dateValue = value || fallback;
  if (!dateValue) return null;

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}T00:00:00`;
};

const getDisplayName = (list, id, getter) => {
  if (!Array.isArray(list) || !id) return;
  const item = list.find((entry) => entry?.id === id || entry?.identityId === id);
  if (!item) return;
  return getter(item);
};

const getListData = (data) => data?.payload || data || [];

const getSingleRecord = (response) => {
  const payload = response?.data ?? response;

  if (Array.isArray(payload)) return payload[0] || null;
  if (Array.isArray(payload?.data)) return payload.data[0] || null;
  if (Array.isArray(payload?.payload)) return payload.payload[0] || null;

  return payload || null;
};

const buildFactoryValues = (order, items = []) => ({
  ...order,
  id: order?.id,
  products: items,
});

const FACTORY_RELEASE_STORAGE_KEY = "factoryReleaseRecordId";

const getFactoryRequestedDeliveryDate = (factoryOrderRecord) => {
  const products = Array.isArray(factoryOrderRecord?.products)
    ? factoryOrderRecord.products
    : [];
  const activeProduct = products.find((product) =>
    Number(product?.currentReleaseQuantity ?? product?.factoryReleasedQuantity ?? 0) > 0
  );
  const activeItem = activeProduct?.productItems?.find((productItem) =>
    Number(productItem?.currentReleaseQuantity ?? productItem?.factoryReleasedQuantity ?? 0) > 0
  );
  const productWithDate = products.find((product) =>
    product?.requestedDeliveryDate || product?.effectiveRequestedDeliveryDate
  );
  const itemWithDate = products
    .flatMap((product) => product?.productItems || [])
    .find((productItem) => productItem?.requestedDeliveryDate || productItem?.effectiveRequestedDeliveryDate);

  return (
    factoryOrderRecord?.requestedDeliveryDate ||
    factoryOrderRecord?.effectiveRequestedDeliveryDate ||
    factoryOrderRecord?.deliveryFromDate ||
    factoryOrderRecord?.scheduleDeliveryDate ||
    activeItem?.requestedDeliveryDate ||
    activeItem?.effectiveRequestedDeliveryDate ||
    activeProduct?.requestedDeliveryDate ||
    activeProduct?.effectiveRequestedDeliveryDate ||
    itemWithDate?.requestedDeliveryDate ||
    itemWithDate?.effectiveRequestedDeliveryDate ||
    productWithDate?.requestedDeliveryDate ||
    productWithDate?.effectiveRequestedDeliveryDate ||
    null
  );
};

const AddNewFactoryOrder = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { menuRights } = useRolePermission("Order");
  const queryFactoryOrderId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("id") || params.get("factoryOrderId");
  }, [location.search]);
  const editFactoryOrderId = location?.state?.id || queryFactoryOrderId;
  const isFactoryOrderEditMode = Boolean(editFactoryOrderId);

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedCustomerOrder, setSelectedCustomerOrder] = useState(null);
  const [customerOrderData, setCustomerOrderData] = useState(null);
  const [factoryOrderData, setFactoryOrderData] = useState(null);
  const [releaseForm, setReleaseForm] = useState(() => createEmptyReleaseForm());
  const [factoryValues, setFactoryValues] = useState(EMPTY_FACTORY_VALUES);
  const [quotUserEntry, setQuotUserEntry] = useState(EMPTY_QUOT_USER_ENTRY);
  const [orderProductTab, setOrderProductTab] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [sourceFactoryRow, setSourceFactoryRow] = useState(null);
  const submitInFlight = useRef(false);
  const [createMsg, setCreateMsg] = useState(null);
  const [show, setShow] = useState(false);
  const [errorShow, setErrorShow] = useState({ show: false, errorMessage: "" });
  const [showAdvancePaymentAmount, setShowAdvancePaymentAmount] = useState(false);
  const [deliveryLeadTime, setDeliveryLeadTime] = useState([]);
  const [activeMainTab, setActiveMainTab] = useState("general"); // "general" | "order-summary"
const userProfile = JSON.parse(localStorage.getItem("userProfile"));
 const isProductQuantityEdit = orderProductTab?.tab === "editorderquantity";
 const { data: customerData } = FetchData("Customer", { skip: isProductQuantityEdit });
  const { data: projectData } = FetchData("project", { skip: isProductQuantityEdit });
  const { data: distributorData } = FetchData("distributor", { skip: isProductQuantityEdit });
  const { data: countryList } = FetchData("country", { skip: isProductQuantityEdit });
  const { data: currencyList } = FetchData("currency");
  const { data: paymentTermsList } = FetchData("paymentterms", { skip: isProductQuantityEdit });
  const { data: deliveryTermsList } = FetchData("deliveryterms", { skip: isProductQuantityEdit });
  const { data: transportModeList } = FetchData("deliverymodes", { skip: isProductQuantityEdit });
  const { data: assignedEngineerList } = FetchData("userprofile", { skip: isProductQuantityEdit });
  const { data: pendingCustomer } = FetchData("factorycustomerpending", {
    skip: isProductQuantityEdit,
    CustomerId: selectedCustomer?.value || undefined,
    UserId: userProfile?.identityId,
    UserType: userProfile?.userType,
    PageNumber: 1,
    PageSize: 25,
  });
  const customerOrders = useMemo(
    () => pendingCustomer?.payload || pendingCustomer || [],
    [pendingCustomer]
  );
  const selectedOrder = customerOrderData?.data || null;
  const factoryOrderRecord = factoryOrderData?.data || null;
  const customers = useMemo(() => getListData(customerData), [customerData]);
  const currencies = useMemo(() => getListData(currencyList), [currencyList]);
const orderFetchRef = useRef(null);
  useEffect(() => {
    setShowAdvancePaymentAmount(Number(selectedOrder?.advancePayment) > 0);
  }, [selectedOrder?.id, selectedOrder?.advancePayment]);
  const selectedFactoryReleaseRecord = useMemo(() => {
    if (!customerOrders?.length) return null;

    const sourceFactoryReleaseId = sourceFactoryRow?.id;
    const sourceCustomerOrderId =
      sourceFactoryRow?.customerOrderId ||
      sourceFactoryRow?.orderId ||
      sourceFactoryRow?.customerOrderProductId;
    const selectedOrderId = selectedOrder?.id;

    return (
      customerOrders.find((item) => {
        return (
          (sourceFactoryReleaseId && String(item?.id) === String(sourceFactoryReleaseId)) ||
          (sourceCustomerOrderId && String(item?.customerOrderId) === String(sourceCustomerOrderId)) ||
          (selectedOrderId && String(item?.customerOrderId) === String(selectedOrderId))
        );
      }) || null
    );
  }, [customerOrders, selectedOrder?.id, sourceFactoryRow]);

  const refreshFactoryOrderData = async (overrideId) => {
    const refreshId =
      overrideId ||
      localStorage.getItem(FACTORY_RELEASE_STORAGE_KEY) ||
      editFactoryOrderId ||
      sourceFactoryRow?.id ||
      selectedFactoryReleaseRecord?.id;
    if (refreshId) {
      await GetOne("factoryOrder", { id: refreshId }, setFactoryOrderData);
    }
  };

const refreshFactoryOrderMarkData = async (markId) => {
    const targetMarkId =
        markId ||
        orderProductTab?.data?.id ||
        sourceFactoryRow?.id;

    if (!targetMarkId) return;

    const refreshId =
        localStorage.getItem(FACTORY_RELEASE_STORAGE_KEY) ||
        editFactoryOrderId ||
        sourceFactoryRow?.id ||
        selectedFactoryReleaseRecord?.id;

    if (refreshId) {
        await GetOne(
            "factoryOrder",
            { id: refreshId },
            setFactoryOrderData
        );
    }

    const markResponse = await GetListAsync(
        "orderproductmark",
        { id: targetMarkId }
    );

    const freshMark = getSingleRecord(markResponse);

    if (!freshMark) return;

    const freshProductItems = Array.isArray(freshMark?.productItems)
        ? freshMark.productItems
        : [];

    setFactoryValues((prev) => {
        const products = Array.isArray(prev?.products)
            ? prev.products
            : [];

        const nextProducts = products.map((product) => {
            const matchedItem = freshProductItems.find((item) => {
                const itemKey =
                    item?.customerOrderProductItemId ||
                    item?.quoteProductItemId ||
                    item?.orderProductItemId ||
                    item?.id;

                const productKey =
                    product?.customerOrderProductItemId ||
                    product?.id ||
                    product?.quoteProductItemId ||
                    product?.orderProductId;

                return String(itemKey) === String(productKey);
            });

            return matchedItem
                ? {
                    ...product,
                    ...matchedItem,
                }
                : product;
        });

        return {
            ...prev,
            products: nextProducts,
        };
    });
};
  const selectedOrderItems = useMemo(
    () =>
      flattenCustomerOrderItems(
        selectedOrder?.products ||
          selectedOrder?.items ||
          selectedOrder?.productItems ||
          selectedOrder?.customerOrderProductItems ||
          []
      ),
    [selectedOrder]
  );

const filteredCustomerOrders = useMemo(() => {
  const selectedOrderOption = selectedCustomerOrder?.value
    ? {
      ...selectedCustomerOrder,
      customerOrderReference:
        selectedCustomerOrder?.customerOrderReference ||
        selectedCustomerOrder?.orderReference ||
        selectedCustomerOrder?.label,
      customerOrderId:
        selectedCustomerOrder?.customerOrderId ||
        selectedCustomerOrder?.value,
    }
    : null;

  if (!selectedCustomer?.value) return selectedOrderOption ? [selectedOrderOption] : [];

  const orders = (customerOrders || [])
    .map((item) => ({
      label: item?.customerOrderReference,
      value: item?.customerOrderId,
      ...item,
    }));

  if (
    selectedOrderOption?.customerOrderId &&
    !orders.some((item) => String(item?.value) === String(selectedOrderOption.customerOrderId))
  ) {
    return [selectedOrderOption, ...orders];
  }

  return orders;
}, [customerOrders, selectedCustomer?.value, selectedCustomerOrder]);

  useEffect(() => {
    const id = editFactoryOrderId || localStorage.getItem(FACTORY_RELEASE_STORAGE_KEY);
    if (editFactoryOrderId) {
      setSourceFactoryRow(location?.state?.row || location?.state || null);
      GetOne("factoryOrder", { id: id }, setFactoryOrderData);
    }
  }, [editFactoryOrderId, location?.state]);

 useEffect(() => {
  const customerOrderId =
    sourceFactoryRow?.customerOrderId ||
    sourceFactoryRow?.orderId ||
    sourceFactoryRow?.customerOrderProductId ||
    factoryOrderRecord?.customerOrderId;

  if (!customerOrderId) return;

  const customerOrderRef =
    sourceFactoryRow?.customerOrderReference ||
    sourceFactoryRow?.orderReference ||
    sourceFactoryRow?.customerOrderNo ||
    sourceFactoryRow?.customerOrderRef ||
    factoryOrderRecord?.customerOrderReference ||
    factoryOrderRecord?.orderReference ||
    "";

  setSelectedCustomerOrder({
    label: customerOrderRef,
    value: customerOrderId,
    customerOrderReference: customerOrderRef,
    customerOrderId,
    id: customerOrderId,
  });

  const fetchKey = String(customerOrderId);

  if (orderFetchRef.current === fetchKey) {
    return;
  }

  orderFetchRef.current = fetchKey;

  GetOne(
    "order",
    { id: customerOrderId },
    setCustomerOrderData
  );
}, [
  sourceFactoryRow?.customerOrderId,
  sourceFactoryRow?.orderId,
  sourceFactoryRow?.customerOrderProductId,
  sourceFactoryRow?.customerOrderReference,
  sourceFactoryRow?.orderReference,
  sourceFactoryRow?.customerOrderNo,
  sourceFactoryRow?.customerOrderRef,
  factoryOrderRecord?.customerOrderId,
  factoryOrderRecord?.customerOrderReference,
  factoryOrderRecord?.orderReference,
]);
  useEffect(() => {
    if (!selectedOrder?.customerId || !customers.length) return;

    const matchedCustomer = customers.find((item) => String(item?.id) === String(selectedOrder?.customerId));
    if (matchedCustomer) {
      setSelectedCustomer({
        label: matchedCustomer?.customerName || "-",
        value: matchedCustomer?.id,
      });
    }
  }, [customers, selectedOrder?.customerId]);

  useEffect(() => {
    if (!selectedOrder) {
      if (isFactoryOrderEditMode && factoryOrderRecord) {
        const requestedDeliveryDate = getFactoryRequestedDeliveryDate(factoryOrderRecord);

        setReleaseForm((prev) => ({
          ...prev,
          scheduledDeliveryDate: factoryOrderRecord?.scheduledDeliveryDate
            ? new Date(factoryOrderRecord.scheduledDeliveryDate)
            : prev?.scheduledDeliveryDate || new Date(),
          deliveryFromDate: requestedDeliveryDate
            ? new Date(requestedDeliveryDate)
            : prev?.deliveryFromDate || null,
          deliveryToDate: factoryOrderRecord?.deliveryToDate
            ? new Date(factoryOrderRecord.deliveryToDate)
            : prev?.deliveryToDate || null,
          remarks: factoryOrderRecord?.remarks || prev?.remarks || "",
        }));
        return;
      }

      setReleaseForm(createEmptyReleaseForm());
      setFactoryValues(EMPTY_FACTORY_VALUES);
      setQuotUserEntry(EMPTY_QUOT_USER_ENTRY);
      return;
    }

    const factoryOrderItemsByCustomerItemId = new Map(
      (factoryOrderRecord?.items || []).map((item) => [
        String(item?.customerOrderProductItemId || item?.id || ""),
        item,
      ])
    );

    const getProductMatchKeys = (item) => [
      item?.customerOrderProductId,
      item?.quoteProductId,
      item?.orderProductId,
      item?.id,
    ].filter((value) => value !== null && value !== undefined && value !== "");

    const getItemMatchKeys = (item) => [
      item?.customerOrderProductItemId,
      item?.quoteProductItemId,
      item?.orderProductItemId,
      item?.customerOrderItemId,
      item?.id,
    ].filter((value) => value !== null && value !== undefined && value !== "");

    const factoryOrderProducts = Array.isArray(factoryOrderRecord?.products)
      ? factoryOrderRecord.products
      : [];
    const defaultRequestedDeliveryDate =
      isFactoryOrderEditMode
        ? getFactoryRequestedDeliveryDate(factoryOrderRecord)
        : new Date();

    const findFactoryProduct = (product) =>
      factoryOrderProducts.find((factoryProduct) =>
        getProductMatchKeys(factoryProduct).some((key) =>
          getProductMatchKeys(product).some((productKey) => String(key) === String(productKey))
        )
      );

    const findFactoryProductItem = (productItem, factoryProduct) =>
      factoryProduct?.productItems?.find((factoryItem) =>
        getItemMatchKeys(factoryItem).some((key) =>
          getItemMatchKeys(productItem).some((itemKey) => String(key) === String(itemKey))
        )
      );

    const getFactoryItemRequestedDeliveryDate = (factoryProduct) => {
      const activeItem = factoryProduct?.productItems?.find((productItem) =>
        Number(productItem?.currentReleaseQuantity ?? productItem?.factoryReleasedQuantity ?? 0) > 0
      );

      return (
        activeItem?.requestedDeliveryDate ??
        activeItem?.effectiveRequestedDeliveryDate ??
        factoryProduct?.requestedDeliveryDate ??
        factoryProduct?.effectiveRequestedDeliveryDate
      );
    };

    const nextFactoryItems = (selectedOrder?.products || []).map((item) => {
      const factoryProduct = findFactoryProduct(item);

      return {
      ...item,
      requestedDeliveryDate:
        (isFactoryOrderEditMode
          ? (
            getFactoryItemRequestedDeliveryDate(factoryProduct)
          )
          : null) ??
        defaultRequestedDeliveryDate ??
        null,
      productItems: Array.isArray(item?.productItems)
        ? item.productItems.map((productItem) => {
          const factoryProductItem = findFactoryProductItem(productItem, factoryProduct);
          return {
            ...productItem,
            requestedDeliveryDate:
              (isFactoryOrderEditMode
                ? (
                  factoryProductItem?.requestedDeliveryDate ??
                  factoryProductItem?.effectiveRequestedDeliveryDate ??
                  factoryProduct?.requestedDeliveryDate ??
                  factoryProduct?.effectiveRequestedDeliveryDate
                )
                : null) ??
              defaultRequestedDeliveryDate ??
              null,
          };
        })
        : item?.productItems,
      quoteQuantity: Number(item?.quoteQuantity ?? item?.quotedQuantity ?? item?.quantity ?? 0),
      customerOrderedQuantity: Number(
        item?.customerOrderedQuantity ??
          item?.lpoQuantity ??
          item?.orderedQuantity ??
          item?.quantity ??
          0
      ),
      releasedQuantity: Number(
        factoryOrderItemsByCustomerItemId.get(String(item?.id || item?.quoteProductItemId || item?.orderProductItemId || ""))?.quantity ??
        item?.releasedQuantity ??
        0
      ),
      lpoQuantity: Number(
          item?.lpoQuantity ??
          item?.orderedQuantity ??
          item?.quantity ??
          0
      ),
    };
    });

    setFactoryValues(buildFactoryValues(selectedOrder, nextFactoryItems));
    setQuotUserEntry({
      specialCharges: Number(selectedOrder?.specialCharges || 0),
      freightCharges: Number(selectedOrder?.freightCharges || 0),
      customDuty: Number(selectedOrder?.customDuty || 0),
      customDutyPercentage: Number(selectedOrder?.customDutyPercentage || 0),
      clearanceCharges: Number(selectedOrder?.clearanceCharges || 0),
      clearanceChargesPercentage: Number(selectedOrder?.clearanceChargesPercentage || 0),
      salesMarginPercentage: Number(selectedOrder?.salesMarginPercentage || 0),
      tax: Number(selectedOrder?.tax || 0),
      taxPercentage: Number(selectedOrder?.taxPercentage || 0),
    });
    setReleaseForm({
      scheduledDeliveryDate: factoryOrderRecord?.scheduledDeliveryDate
        ? new Date(factoryOrderRecord.scheduledDeliveryDate)
        : new Date(),
      deliveryFromDate: defaultRequestedDeliveryDate
        ? new Date(defaultRequestedDeliveryDate)
        : null,
      deliveryToDate: isFactoryOrderEditMode && factoryOrderRecord?.deliveryToDate
        ? new Date(factoryOrderRecord?.deliveryToDate)
        : null,
      remarks: factoryOrderRecord?.remarks || selectedOrder?.remarks || "",
        items: selectedOrderItems.map((item) => ({
        customerOrderProductItemId:
          (
            item?.customerOrderProductItemId ??
            item?.quoteProductItemId ??
            item?.orderProductItemId ??
            item?.id ??
            0
          ),
        markName: item?.markName || "-",
        productName: item?.productName || "-",
        groupName: item?.groupName || "-",
        availableQuantity: Number(
          item?.customerOrderedQuantity ??
            item?.lpoQuantity ??
            item?.orderedQuantity ??
            item?.quoteQuantity ??
            item?.quantity ??
            0
        ),
        quantity: Number(
          item?.releasedQuantity ??
            item?.lpoQuantity ??
            item?.customerOrderedQuantity ??
            item?.balanceQuantity ??
            item?.quoteQuantity ??
            item?.quantity ??
            0
        ),
        })),
    });
  }, [selectedOrder, selectedOrderItems, factoryOrderRecord, isFactoryOrderEditMode]);

  const handleDeliveryFromDateChange = (date) => {
  setReleaseForm((prev) => {
    const previousDate = prev?.deliveryFromDate;

    setFactoryValues((currentValues) => ({
      ...currentValues,
      products: (currentValues?.products || []).map((product) => {
        const shouldUpdateProductDate =
          !product?.requestedDeliveryDate ||
          toLocalDateIso(product?.requestedDeliveryDate) === toLocalDateIso(previousDate);

        return {
          ...product,
          requestedDeliveryDate: shouldUpdateProductDate ? date : product?.requestedDeliveryDate,
          productItems: Array.isArray(product?.productItems)
            ? product.productItems.map((item) => {
              const shouldUpdateItemDate =
                !item?.requestedDeliveryDate ||
                toLocalDateIso(item?.requestedDeliveryDate) === toLocalDateIso(previousDate);

              return {
                ...item,
                requestedDeliveryDate: shouldUpdateItemDate ? date : item?.requestedDeliveryDate,
              };
            })
            : product?.productItems,
        };
      }),
    }));

    return {
      ...prev,
      deliveryFromDate: date,
    };
  });

  clearErrorField("deliveryFromDate");
};

const handleDeliveryToDateChange = (date) => {
  setReleaseForm((prev) => ({
    ...prev,
    deliveryToDate: date,
  }));

  clearErrorField("deliveryToDate");
};

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("pathfinderHeaderMetaChange", {
        detail: {
          pathname: location.pathname,
          title: selectedOrder?.orderReference
            ? `Factory Order : ${selectedOrder.orderReference}`
            : "New Factory Order",
          backTarget: {
            pathname: "/factory-order-list",
          },
        },
      })
    );

    return () => {
      window.dispatchEvent(
        new CustomEvent("pathfinderHeaderMetaChange", {
          detail: {
            pathname: location.pathname,
            clear: true,
          },
        })
      );
    };
  }, [location.pathname, selectedOrder?.orderReference]);

  const clearErrorField = (fieldName) => {
    setErrors((prev) => {
      if (!prev?.[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const handleCustomerOrderChange = (selectedOption) => {
    setSelectedCustomerOrder(selectedOption);

    if (!selectedOption) {
      setCustomerOrderData(null);
      setReleaseForm(createEmptyReleaseForm());
      setFactoryValues(EMPTY_FACTORY_VALUES);
      setQuotUserEntry(EMPTY_QUOT_USER_ENTRY);
      clearErrorField("customerOrderRef");
      return;
    }

    GetOne("order", { id: selectedOption?.value }, setCustomerOrderData);
    clearErrorField("customerOrderRef");
  };

  const handleCustomerChange = (selectedOption) => {
    setSelectedCustomer(selectedOption);
    setSelectedCustomerOrder(null);
    setCustomerOrderData(null);
    setReleaseForm(createEmptyReleaseForm());
    setFactoryValues(EMPTY_FACTORY_VALUES);
    setQuotUserEntry(EMPTY_QUOT_USER_ENTRY);
    clearErrorField("customerName");
    clearErrorField("customerOrderRef");
  };

  const handleItemQuantityChange = (index, value) => {
    setReleaseForm((prev) => {
      const items = [...(prev?.items || [])];
      items[index] = {
        ...items[index],
        quantity: value,
      };
      return {
        ...prev,
        items,
      };
    });
    clearErrorField("items");
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setReleaseForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    clearErrorField(name);
  };

  const validate = () => {
    const nextErrors = {};

    if (!selectedCustomer?.value) {
      nextErrors.customerName = "Customer name is required.";
    }

    if (!selectedCustomerOrder?.value) {
      nextErrors.customerOrderRef = "Customer order reference is required.";
    }

    if (!releaseForm?.scheduledDeliveryDate) {
      nextErrors.scheduledDeliveryDate = "Scheduled delivery date is required.";
    }

    return nextErrors;
  };

  const HandleOrderSummaryTabClick = (e) => {
    const validationErrors = validate();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      e.preventDefault();
      e.stopPropagation();
    }
    setActiveMainTab("order-summary");
  };

  const handleSubmit = () => {
    if (submitInFlight.current || loading) return;

    const validationErrors = validate();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    submitInFlight.current = true;
    setLoading(true);

    const productsSource = Array.isArray(factoryValues?.products)
      ? factoryValues.products
      : [];

    const itemsSource = productsSource.length > 0
      ? productsSource.flatMap((product) =>
          Array.isArray(product?.productItems) && product.productItems.length > 0
            ? product.productItems.map((item) => ({
              ...item,
              __parentProduct: product,
            }))
            : [{
              ...product,
              __parentProduct: product,
            }]
        )
      : [];
 
    const scheduledDeliveryDate = toLocalDateIso(releaseForm.scheduledDeliveryDate);
    const requestedDeliveryDate =
      toLocalDateIso(releaseForm.deliveryFromDate) ||
      scheduledDeliveryDate;

    const toIsoDate = (value, fallback = requestedDeliveryDate) => {
      return toLocalDateIso(value, fallback) || fallback;
    };

    const getDeliveryDatePatch = (value, fallback = requestedDeliveryDate) => {
      const requestedDate = toLocalDateIso(value, fallback);
      if (!requestedDate) {
        return { clearRequestedDeliveryDate: true };
      }

      return { requestedDeliveryDate: requestedDate };
    };

    const getCustomerOrderProductId = (item) =>
      item?.customerOrderProductId ??
      item?.orderProductId ??
      item?.quoteProductId ??
      item?.quoteProductItemId ??
      item?.id ??
      0;

    const getCustomerOrderProductItemId = (item) =>
      item?.customerOrderProductItemId ??
      item?.quoteProductItemId ??
      item?.orderProductItemId ??
      item?.id ??
      0;

    const getReleaseQuantity = (item) => Number(
      item?.currentReleasedQuantity ??
      item?.currentReleaseQuantity ??
      item?.factoryReleasedQuantity ??
      item?.releasedQuantity ??
      0
    ) || 0;

    const productDeliveryDates = Object.values(
      productsSource.reduce((acc, product) => {
        const customerOrderProductId = getCustomerOrderProductId(product);
        if (!customerOrderProductId || acc[customerOrderProductId]) {
          return acc;
        }

        acc[customerOrderProductId] = {
          customerOrderProductId,
          ...getDeliveryDatePatch(product?.requestedDeliveryDate),
        };

        return acc;
      }, {})
    );

    const payload = {
      id: editFactoryOrderId || factoryOrderRecord?.id || selectedCustomerOrder?.value,
      facilityId:
        releaseForm?.facilityId ??
        factoryOrderRecord?.facilityId ??
        selectedOrder?.facilityId ??
        "",
      scheduledDeliveryDate,
      deliveryFromDate: requestedDeliveryDate,
      deliveryToDate: toLocalDateIso(releaseForm.deliveryToDate),
      remarks: releaseForm?.remarks || "",
      additionalCharges: Number(
        releaseForm?.additionalCharges ??
        factoryOrderRecord?.additionalCharges ??
        selectedOrder?.additionalCharges ??
        0
      ),
      deliveryCharges: Number(
        releaseForm?.deliveryCharges ??
        factoryOrderRecord?.deliveryCharges ??
        selectedOrder?.deliveryCharges ??
        quotUserEntry?.freightCharges ??
        0
      ),
      clearanceCharges: Number(
        releaseForm?.clearanceCharges ??
        factoryOrderRecord?.clearanceCharges ??
        selectedOrder?.clearanceCharges ??
        quotUserEntry?.clearanceCharges ??
        0
      ),
      specialColorCharges: Number(
        releaseForm?.specialColorCharges ??
        factoryOrderRecord?.specialColorCharges ??
        selectedOrder?.specialColorCharges ??
        0
      ),
      taxPercentage: Number(
        releaseForm?.taxPercentage ??
        factoryOrderRecord?.taxPercentage ??
        selectedOrder?.taxPercentage ??
        quotUserEntry?.taxPercentage ??
        0
      ),
      tax: Number(
        releaseForm?.tax ??
        factoryOrderRecord?.tax ??
        selectedOrder?.tax ??
        quotUserEntry?.tax ??
        0
      ),
      productDeliveryDates,
      items: itemsSource.map((item) => ({
        customerOrderProductItemId: getCustomerOrderProductItemId(item),
        quoteProductItemId: item?.quoteProductItemId ?? item?.__parentProduct?.quoteProductItemId ?? 0,
        orderProductItemId: item?.orderProductItemId ?? item?.__parentProduct?.orderProductItemId ?? 0,
        customerOrderItemId: item?.customerOrderItemId ?? item?.id ?? 0,
        quantity: getReleaseQuantity(item),
        notes: item?.notes || "",
        tagReference: item?.tagReference || "",
        ...getDeliveryDatePatch(
          item?.requestedDeliveryDate,
          toIsoDate(item?.__parentProduct?.requestedDeliveryDate)
        ),
      })),
    };

    const saveAction = isFactoryOrderEditMode ? UpdateEntry : CreateEntry;
    const saveResource = isFactoryOrderEditMode ? "factoryOrder" : "factorycreateorder";
    saveAction(
      saveResource,
      payload,
      setCreateMsg,
      isFactoryOrderEditMode ? "Factory order updated successfully" : "Factory order created successfully",
      "Failed to save factory order"
    );
  };

useEffect(() => {
    if (!createMsg) return;

    const handleSuccess = async () => {
        if (createMsg?.status === 400) {
            setErrorShow({
                show: true,
                errorMessage: createMsg?.successData,
            });

            setLoading(false);
            submitInFlight.current = false;
            return;
        }

        if (
            CheckNull(createMsg?.message) === "Factory order created successfully" ||
            CheckNull(createMsg?.message) === "Factory order updated successfully"
        ) {
            const savedFactoryOrderId =
                createMsg?.successData?.id ||
                editFactoryOrderId ||
                factoryOrderRecord?.id ||
                selectedCustomerOrder?.value ||
                sourceFactoryRow?.id ||
                "";

            if (savedFactoryOrderId) {
                localStorage.setItem(
                    FACTORY_RELEASE_STORAGE_KEY,
                    savedFactoryOrderId
                );

                // IMPORTANT: wait for latest factory order data
                await refreshFactoryOrderData(savedFactoryOrderId);
            }

            setShow(true);
            setLoading(false);
            submitInFlight.current = false;
        }
    };

    handleSuccess();
}, [createMsg]);

  const SuccessDoneClick = () => {
    setShow(false);
  };

  const ErrorDoneClick = () => {
    setErrorShow({ show: false, errorMessage: "" });
    setCreateMsg(null);
  };

  const selectedOrderProjectName = getDisplayName(
    projectData?.payload || projectData || [],
    selectedOrder?.projectId,
    (item) => item?.projectName
  );
  const selectedOrderDistributorName = getDisplayName(
    distributorData?.payload || distributorData || [],
    selectedOrder?.distributorId,
    (item) => item?.distributorName
  );
  const selectedOrderCountryName = getDisplayName(
    countryList?.payload || countryList || [],
    selectedOrder?.countryId,
    (item) => item?.name
  );
  const selectedOrderCurrencyName = getDisplayName(
    currencyList?.payload || currencyList || [],
    selectedOrder?.currencyId,
    (item) => item?.shortCode || item?.currencyName
  );
  const selectedOrderPaymentTerms = getDisplayName(
    paymentTermsList?.payload || paymentTermsList || [],
    selectedOrder?.paymentTermId,
    (item) => item?.paymentDescription
  );
  const selectedOrderDeliveryTerms = getDisplayName(
    deliveryTermsList?.payload || deliveryTermsList || [],
    selectedOrder?.deliveryTermId,
    (item) => item?.deliveryDescription
  );
  const selectedOrderDeliveryLeadTime =
    factoryOrderRecord?.deliveryLeadTime ||
    selectedOrder?.deliveryLeadTime ||
    getDisplayName(
      deliveryLeadTimeValues,
      factoryOrderRecord?.deliveryLeadTimeId || selectedOrder?.deliveryLeadTimeId,
      (item) => item?.label
    );
  const selectedOrderTransport = getDisplayName(
    transportModeList?.payload || transportModeList || [],
    selectedOrder?.modeOfTransportId,
    (item) => item?.transportationMode
  );
  const selectedOrderSalesEngineer = getDisplayName(
    assignedEngineerList?.payload || assignedEngineerList || [],
    selectedOrder?.salesEngineerId,
    (item) =>
      `${item?.firstName || ""} ${item?.lastName || ""}`.trim() || item?.fullName
  );
  const selectedOrderBillingCustomer = getDisplayName(
    customerData?.payload || customerData || [],
    selectedOrder?.billingCustomerId || selectedOrder?.customerId,
    (item) => item?.customerName
  );
  const selectedCurrencyDetail = currencies.find(
    (item) => String(item?.id) === String(selectedOrder?.currencyId)
  );

  const selectedOrderTypeLabel =
    selectedOrder?.orderType === 1
      ? "Job In Hand"
      : selectedOrder?.orderType === 2
        ? "Tender"
        : "-";

  const handleCancel = () => {
    navigate("/factory-order-list");
  };

    useEffect(() => {
      const leadTimeMatch = deliveryLeadTimeValues?.find(item => Number(item?.value) === Number(factoryOrderRecord?.deliveryLeadTimeId || selectedOrder?.deliveryLeadTimeId));
      setDeliveryLeadTime(leadTimeMatch || null)
    }, [deliveryLeadTimeValues, factoryOrderRecord?.deliveryLeadTimeId, selectedOrder?.deliveryLeadTimeId])

  return (
    <div className="container-fluid">
      <section id="addNewOrderSec">
        <div className="row">
          <div className="col-12 p-0 my-auto">
            <div className="card">
              {orderProductTab?.tab !== "editorderquantity" && (
                <div className="card-header">
                  <div className="d-flex justify-content-start">
                    <div className="nav nav-pills order-tabs" id="pills-tab" role="tablist">
                      <a
                        className={`nav-link${activeMainTab === "general" ? " active" : ""}`}
                        id="pills-general-tab"
                        data-toggle="pill"
                        href="#pills-general"
                        role="tab"
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveMainTab("general");
                        }}
                      >
                        <i className="far fa-file-alt mr-2"></i>
                        General
                      </a>

                      <a
                        className={`nav-link${activeMainTab === "order-summary" ? " active" : ""}`}
                        id="pills-order-summary-tab"
                        data-toggle="pill"
                        href="#pills-order-summary"
                        role="tab"
                        onClick={HandleOrderSummaryTabClick}
                      >
                        <i className="fas fa-boxes mr-2"></i>
                        Order Summary
                      </a>
                    </div>
                  </div>
                </div>
              )}

              <div className="card-body">
                <div className="tab-content" id="pills-tabContent">
                  <div
                    className={`tab-pane fade${activeMainTab === "general" ? " show active" : ""}`}
                    id="pills-general"
                    role="tabpanel"
                    aria-labelledby="pills-general-tab"
                  >
                    <div className="row">
                      <div className="col-xxl-6 col-xl-6 col-lg-12 col-md-12 col-sm-12 col-xs-12 left-col">
                        <div className="card">
                          <div className="card-header">
                            <h2>Customer & Contact</h2>
                          </div>
                          <div className="card-body">
                            <div className="row">
                              <div className="col-12 mb-2">
                                <label>Customer Name <span className="mandatory">*</span></label>
                                <DropDownComponent
                                  placeHolder={PlaceHolder.SELECT_CUSTOMER}
                                  pageName="factoryOrder"
                                  dpDownName="customer"
                                  stateData={customers}
                                  controlName="customerName"
                                  handleChange={handleCustomerChange}
                                  value={selectedCustomer}
                                  valueId={selectedCustomer?.value}
                                  setState={setSelectedCustomer}
                                  multiSelect={false}
                                  isDisabled={isFactoryOrderEditMode}
                                />
                                {errors.customerName && <p className="form-error">{errors.customerName}</p>}
                              </div>

                              <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 col-sm-12 col-xs-12 mb-2">
                                <label className="font-weight-bold">
                                  Customer Order Reference <span className="mandatory">*</span>
                                </label>
                                <DropDownComponent
                                  placeHolder={PlaceHolder.SELECT_CUSTOMER_ORDER_REF}
                                  pageName="factoryOrder"
                                  dpDownName="orderRef"
                                  stateData={filteredCustomerOrders}
                                  controlName="customerOrderRef"
                                  handleChange={handleCustomerOrderChange}
                                  value={selectedCustomerOrder}
                                  valueId={selectedCustomerOrder?.value}
                                  setState={setSelectedCustomerOrder}
                                  multiSelect={false}
                                  isDisabled={!selectedCustomer?.value || isFactoryOrderEditMode}
                                />
                                {errors.customerOrderRef && <p className="form-error">{errors.customerOrderRef}</p>}
                              </div>

                              <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 col-sm-12 col-xs-12 mb-2">
                                <label>Factory Order Date <span className="mandatory">*</span></label>
                                <DatePicker
                                  selected={releaseForm.scheduledDeliveryDate}
                                  onChange={(date) =>
                                    setReleaseForm((prev) => ({
                                      ...prev,
                                      scheduledDeliveryDate: date,
                                    }))
                                  }
                                  customInput={<ExampleCustomInput />}
                                  dateFormat="dd/MM/yyyy"
                                />
                              </div>

                              <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 col-sm-12 col-xs-12 mb-2">
                                <label>PO Date</label>
                                <input className="form-control" readOnly value={formatDateValue(selectedOrder?.purchaseOrderDate)} />
                              </div>

                              <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 col-sm-12 col-xs-12 mb-2">
                                <label>Customer PO Number <span className="mandatory">*</span></label>
                                <input className="form-control" readOnly value={selectedOrder?.purchaseOrderReference} />
                              </div>

                              <div className="col-12 mb-2">
                                <label>Customer Address <span className="mandatory">*</span></label>
                                <textarea className="form-control" rows="5" readOnly value={selectedOrder?.customerAddress} />
                              </div>

                              <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 col-sm-12 col-xs-12 mb-2">
                                <label>Currency <span className="mandatory">*</span></label>
                                <input className="form-control" readOnly value={selectedOrderCurrencyName} />
                              </div>

                              <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 col-sm-12 col-xs-12 mb-2">
                                <label>Conversion rate <span className="mandatory">*</span></label>
                                <input className="form-control" readOnly value={selectedOrder?.conversionRate} />
                              </div>

                              <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 col-sm-12 col-xs-12 mb-2">
                                <label>Phone Number <span className="mandatory">*</span></label>
                                <input className="form-control" readOnly value={selectedOrder?.phone} />
                              </div>

                              <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 col-sm-12 col-xs-12 mb-2">
                                <label>Email Address</label>
                                <input className="form-control" readOnly value={selectedOrder?.emailAddress} />
                              </div>

                              <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 col-sm-12 col-xs-12 mb-2">
                                <label>Country <span className="mandatory">*</span></label>
                                <input className="form-control" readOnly value={selectedOrderCountryName} />
                              </div>

                              <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 col-sm-12 col-xs-12 mb-2">
                                <label>Distributor</label>
                                <input className="form-control" readOnly value={selectedOrderDistributorName} />
                              </div>

                              <div className="col-12 mb-2">
                                <label>Sales Engineer <span className="mandatory">*</span></label>
                                <input className="form-control" readOnly value={selectedOrderSalesEngineer} />
                              </div>

                              <div className="col-12 mb-2">
                                <label>Other Details</label>
                                <textarea className="form-control" rows="4" readOnly value={selectedOrder?.otherConditions} />
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="card mt-3">
                         
                          <div className="card-body">
                            <div className="row">

                              <div className="col-12 mb-2">
                                <label>Remarks</label>
                                <textarea
                                  className="form-control"
                                  rows="4"
                                  name="remarks"
                                  placeholder="Enter remarks"
                                  value={releaseForm?.remarks}
                                  onChange={handleFormChange}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-xxl-6 col-xl-6 col-lg-12 col-md-12 col-sm-12 col-xs-12 right-col">
                        <div className="card">
                          <div className="card-header">
                            <h2>Project & Commercial Terms</h2>
                          </div>
                          <div className="card-body">
                            <div className="row">
                              <div className="col-12 mb-2">
                                <label>Project Name <span className="mandatory">*</span></label>
                                <input className="form-control" readOnly value={selectedOrderProjectName} />
                              </div>
                              <div className="col-12 mb-2">
                                <label>Customer Order Type <span className="mandatory">*</span></label>
                                <input className="form-control" readOnly value={selectedOrderTypeLabel} />
                              </div>
                              <div className="col-12 mb-2">
                                <label>Bill To <span className="mandatory">*</span></label>
                                <input className="form-control" readOnly value={selectedOrderBillingCustomer} />
                              </div>
                              <div className="col-12 mb-2">
                                <label>Ship To <span className="mandatory">*</span></label>
                                <textarea className="form-control" rows="5" readOnly value={selectedOrder?.billingAddress} />
                              </div>
                              <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 col-sm-12 col-xs-12 mb-2">
                                <label>Contact Person <span className="mandatory">*</span></label>
                                <input className="form-control" readOnly value={selectedOrder?.contactPerson} />
                              </div>
                              <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 col-sm-12 col-xs-12 mb-2">
                                <label>Delivery Contact Number <span className="mandatory">*</span></label>
                                <input className="form-control" readOnly value={selectedOrder?.deliveryContactNo} />
                              </div>
                              <div className="col-12 mb-2">
                                <label>Payment Terms <span className="mandatory">*</span></label>
                                <input className="form-control" readOnly value={selectedOrder?.paymentTerms || selectedOrderPaymentTerms} />
                              </div>
                              <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 col-sm-12 col-xs-12 mb-2">
                                <label>Advance Payment</label>
                                <AmountModeField
                                  id="factory-order-advance-payment"
                                  mode={showAdvancePaymentAmount ? "$" : "%"}
                                  name="factoryOrderAdvancePayment"
                                  onModeToggle={() => setShowAdvancePaymentAmount((current) => !current)}
                                  placeholder="-"
                                  readOnly
                                  type="text"
                                  value={
                                    showAdvancePaymentAmount
                                      ? selectedOrder?.advancePayment || ""
                                      : selectedOrder?.paymentPercentage || ""
                                  }
                                />
                              </div>
                              <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 col-sm-12 col-xs-12 mb-2">
                                <label>Credit Days <span className="mandatory">*</span></label>
                                <input className="form-control" readOnly value={selectedOrder?.creditDays} />
                              </div>
                              <div className="col-12 mb-2">
                                <label>Delivery Terms <span className="mandatory">*</span></label>
                                <input className="form-control" readOnly value={selectedOrder?.deliveryTerms || selectedOrderDeliveryTerms} />
                              </div>
                              <div className="col-12 mb-2">
                                <label>Delivery Lead Time <span className="mandatory">*</span></label>
                                <input className="form-control" readOnly value={selectedOrderDeliveryLeadTime ? selectedOrderDeliveryLeadTime : deliveryLeadTime?.label} />
                              </div>
                              <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 col-sm-12 col-xs-12 mb-2">
                                <label>Mode of Transport <span className="mandatory">*</span></label>
                                <input className="form-control" readOnly value={selectedOrderTransport} />
                              </div>
                              <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 col-sm-12 col-xs-12 mb-2">
                                <label>Destination Country</label>
                                <input className="form-control" readOnly value={selectedOrderCountryName} />
                              </div>
                              <div className="col-12 mb-2">
  <label>Request Delivery Date</label>

  <DatePicker
    selected={releaseForm?.deliveryFromDate}
    onChange={handleDeliveryFromDateChange}
    customInput={<ExampleCustomInput />}
    dateFormat="dd/MM/yyyy"
  />
</div>
                              {/* <div className="col-12 mb-2">
  <label>Request Delivery Date (To)</label>

  <DatePicker
    selected={releaseForm?.deliveryToDate}
    onChange={handleDeliveryToDateChange}
    customInput={<ExampleCustomInput />}
    dateFormat="dd/MM/yyyy"
    minDate={releaseForm?.deliveryFromDate || null}
  />
</div> */}
                            </div>
                          </div>
                        </div>
                      </div>
                     
                    </div>
                  </div>

                  <FactoryOrderProduct
                    menuRights={menuRights}
                    values={factoryValues}
                    currencyDetail={selectedCurrencyDetail}
                    setValues={setFactoryValues}
                    setQuotUserEntry={setQuotUserEntry}
                    quotUserEntry={quotUserEntry}
                    handleSubmit={handleSubmit}
                    loading={loading}
                    orderProductTab={orderProductTab}
                    setOrderProductTab={setOrderProductTab}
                    factoryReleaseRecordId={factoryOrderRecord?.id || selectedFactoryReleaseRecord?.id || sourceFactoryRow?.id || null}
                    refreshFactoryOrderMarkData={refreshFactoryOrderMarkData}
                    refreshFactoryOrderData={refreshFactoryOrderData}
                    activeMainTab={activeMainTab}
                    factoryOrderRecord={factoryOrderRecord}
                    selectedOrder={selectedOrder}
                    releaseForm={releaseForm}
                    isFactoryOrderEditMode={isFactoryOrderEditMode}
                    handleDeliveryFromDateChange={handleDeliveryFromDateChange}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SaveSuccessModel
        show={show}
        createMsg={createMsg}
        setShow={setShow}
        SuccessDoneClick={SuccessDoneClick}
      />
      <ErrorMessage
        Show={errorShow?.show}
        errorMessage={errorShow?.errorMessage}
        ErrorDoneClick={ErrorDoneClick}
      />
    </div>
  );
};

export default AddNewFactoryOrder;
